/**
 * Frontend domain utilities for dynamic URL configuration
 */

/**
 * Get the current domain information
 * @returns {Object} Domain information object
 */
export function getCurrentDomain() {
  return {
    protocol: window.location.protocol,
    hostname: window.location.hostname,
    port: window.location.port,
    host: window.location.host,
    origin: window.location.origin,
    href: window.location.href,
    pathname: window.location.pathname
  };
}

/**
 * Determine if we're running in a proxy environment
 * @returns {boolean} True if likely behind a proxy
 */
export function isLikelyBehindProxy() {
  const hostname = window.location.hostname;
  
  // Check for common proxy domain patterns
  const proxyPatterns = [
    /\.cs\.gt$/,      // Your specific domain pattern
    /\.local$/,       // Common local development proxy
    /\.proxy$/,       // Common proxy domain
    /\.internal$/     // Internal domains
  ];
  
  return proxyPatterns.some(pattern => pattern.test(hostname));
}

/**
 * Get the appropriate API base URL for the current environment
 * @returns {string} API base URL
 */
export function getApiBaseUrl() {
  // If explicitly configured via environment variable, use it
  if (process.env.REACT_APP_API_URL) {
    console.log('Using configured API URL:', process.env.REACT_APP_API_URL);
    return process.env.REACT_APP_API_URL;
  }

  const currentDomain = getCurrentDomain();
  
  // In production, use smart detection
  if (process.env.NODE_ENV === 'production') {
    // If served from the same domain (typical production setup), use relative URLs
    if (currentDomain.hostname !== 'localhost' && currentDomain.hostname !== '127.0.0.1') {
      console.log('Production mode: Using relative URLs for same-domain deployment');
      return ''; // Relative URLs - will use current domain
    }
    
    // Fallback for local production testing
    console.log('Production mode: Using current domain for API calls');
    return currentDomain.origin;
  }

  // Development fallback
  console.log('Development mode: Using localhost:5001');
  return 'http://localhost:5001';
}

/**
 * Generate a full URL for a specific path
 * @param {string} path - Path to append
 * @returns {string} Full URL
 */
export function generateFullUrl(path) {
  const currentDomain = getCurrentDomain();
  const normalizedPath = path.startsWith('/') ? path : `/${path}`;
  return `${currentDomain.origin}${normalizedPath}`;
}

/**
 * Check if the current domain matches a pattern
 * @param {string} pattern - Pattern to match (supports basic wildcards)
 * @returns {boolean} True if matches
 */
export function matchesDomainPattern(pattern) {
  const hostname = window.location.hostname;
  
  if (pattern === '*') return true;
  
  // Convert wildcard pattern to regex
  const regexPattern = pattern
    .replace(/\./g, '\\.')
    .replace(/\*/g, '.*');
  
  const regex = new RegExp(`^${regexPattern}$`, 'i');
  return regex.test(hostname);
}

/**
 * Get domain configuration for debugging
 * @returns {Object} Configuration object
 */
export function getDomainConfig() {
  const currentDomain = getCurrentDomain();
  const apiBaseUrl = getApiBaseUrl();
  
  return {
    currentDomain,
    apiBaseUrl,
    environment: process.env.NODE_ENV,
    configuredApiUrl: process.env.REACT_APP_API_URL,
    isLikelyBehindProxy: isLikelyBehindProxy(),
    timestamp: new Date().toISOString()
  };
}

/**
 * Log domain configuration for debugging
 */
export function logDomainConfig() {
  const config = getDomainConfig();
  console.group('🌐 Domain Configuration');
  console.log('Current Domain:', config.currentDomain);
  console.log('API Base URL:', config.apiBaseUrl);
  console.log('Environment:', config.environment);
  console.log('Configured API URL:', config.configuredApiUrl);
  console.log('Behind Proxy?:', config.isLikelyBehindProxy);
  console.groupEnd();
  return config;
}